/**
 * Custom hook for order operations
 * Wraps Firebase order services with loading/error state
 */
import { useCallback, useState } from 'react';
import { useAuth } from './useAuth';
import {
  createOrder,
  getOrderById,
  getOrdersByUserId,
  updateOrderStatus,
} from '@/services/firebase/orders.service';
import { Order, OrderStatus } from '@/types/order.types';
import { CreateOrderData } from '@/services/firebase/orders.service';

interface UseOrdersReturn {
  orders: Order[];
  currentOrder: Order | null;
  isLoading: boolean;
  error: string | null;
  fetchOrders: () => Promise<void>;
  fetchOrder: (orderId: string) => Promise<Order | null>;
  placeOrder: (data: CreateOrderData) => Promise<Order>;
  cancelOrder: (orderId: string) => Promise<void>;
}

/**
 * useOrders - Hook for customer-facing order operations
 *
 * @example
 * const { orders, fetchOrders, placeOrder } = useOrders();
 */
export const useOrders = (): UseOrdersReturn => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await getOrdersByUserId(user.id);
      setOrders(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch orders';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const fetchOrder = useCallback(async (orderId: string): Promise<Order | null> => {
    setIsLoading(true);
    setError(null);
    try {
      const order = await getOrderById(orderId);
      setCurrentOrder(order);
      return order;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch order';
      setError(message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const placeOrder = useCallback(async (data: CreateOrderData): Promise<Order> => {
    setIsLoading(true);
    setError(null);
    try {
      const order = await createOrder(data);
      setCurrentOrder(order);
      return order;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to place order';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const cancelOrder = useCallback(async (orderId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await updateOrderStatus(orderId, OrderStatus.CANCELLED, 'Cancelled by customer');
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, status: OrderStatus.CANCELLED } : o
        )
      );
      if (currentOrder?.id === orderId) {
        setCurrentOrder((prev) =>
          prev ? { ...prev, status: OrderStatus.CANCELLED } : null
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel order';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentOrder?.id]);

  return {
    orders,
    currentOrder,
    isLoading,
    error,
    fetchOrders,
    fetchOrder,
    placeOrder,
    cancelOrder,
  };
};
